import React, { useState, useMemo } from 'react';
import { X } from 'lucide-react';

const DiscoverModal = ({ isOpen, onClose }) => {
  const discoverCards = useMemo(() => [
    {
      id: 1,
      title: "Journals",
      badgeColor: "#141414f4",
      userMessages: ["Hi Preptune!", "I'd like to write about my day."],
      aiResponse: "Hey, I'm here to listen",
      description: "In Preptune the control is yours. Lock an entry after joruanling and it stays private and hidden from Preptune.",
      gradient: "linear-gradient(250deg, rgba(255, 218, 185, 1) 0%, rgba(255, 239, 213, 1) 53%, rgba(255, 248, 241, 0.3) 60%, #fff5e8ff 60%, #fff9f0ff 100%)",
      features: [
        "Fully private and secure",
        "Track your mood over time",
        "Discover insights from your entries"
      ]
    },
    {
      id: 2,
      title: "Custom Podcasts",
      badgeColor: "#141414f4",
      userMessages: ["Create a podcast", "Make it about the Solar System"],
      aiResponse: "Creating your podcast now",
      description: "Turn any topic into an audio show. Pick what interests you and get a personalized podcast made just for you.",
      gradient: "linear-gradient(250deg, rgba(203, 224, 255, 1) 0%, rgba(220, 235, 255, 1) 53%, rgba(236, 241, 251, 0.3) 60%, #e9f9ffff 60%, #eaf2ffff 100%)",
      features: [
        "Different host voices to choose from",
        "Create podcasts on any topic instantly",
        "Audio updates on news you care about"
      ]
    },
    {
      id: 3,
      title: "Persistent Memory",
      badgeColor: "#141414f4",
      userMessages: ["When did I last call mom?"],
      aiResponse: "Earlier this week, on Tuesday",
      description: "Preptune picks up on your style and what's important, so you don't have to repeat yourself. You stay in control and can edit its memory whenever you want.",
      gradient: "linear-gradient(250deg, rgba(255, 230, 244, 1) 0%, rgba(255, 240, 253, 1) 53%, rgba(254, 248, 253, 0.3) 60%, #fef9feff 60%, #fffafeff 100%)",
      features: [
        "Remembers past conversations",
        "Learns your preferences",
        "Recalls important details and routines"
      ]
    },
    {
      id: 4,
      title: "Study Tools",
      badgeColor: "#141414f4",
      userMessages: ["I need help studying", "Create a quiz on biology"],
      aiResponse: "Let's create your study plan 💪",
      description: "Make quizzes and flashcards from any topic or file. While you take them, Preptune lets you ask questions, clarify concepts, and dive deeper with real-time voice assistance.",
      gradient: "linear-gradient(250deg, rgba(243, 232, 255, 1) 0%, rgba(250, 240, 255, 1) 53%, rgba(253, 247, 255, 0.3) 60%, #fdf9ffff 60%, #fefbffff 100%)",
      features: [
        "Create quizzes from your documents",
        "24/7 voice assitance on any quiz",
        "Get instant feedback on your answers"
      ]
    },
    {
      id: 5,
      title: "Smart Planner",
      badgeColor: "#141414f4",
      userMessages: ["Preptune", "Remind me to study spanish daily"],
      aiResponse: "Ok, I'll organize your schedule",
      description: "Never forget what's important. Set events, get reminders, and let Preptune help you stay organized.",
      gradient: "linear-gradient(250deg, rgba(211, 255, 239, 1) 0%, rgba(220, 255, 245, 1) 53%, rgba(236, 251, 248, 0.3) 60%, #e9fffbff 60%, #eafffcff 100%)",
      features: [
        "Smart reminders at the right time",
        "Organizes tasks by importance",
        "Easy and quick to schedule"
      ]
    }
  ], []);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(100, 100, 100, 0.5)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'flex-end',
      opacity: 1,
    }}>
      <div style={{
        width: '100%',
        height: '85vh',
        background: 'white',
        borderRadius: '24px 24px 0 0',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        zIndex: 10000,
      }}>
        
        {/* Header */}
        <div style={{
          padding: '20px 20px 16px 20px',
          borderBottom: '1px solid #e5e7eb',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: '#fafafa',
          flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '50%',
              width: '30px',
              height: '30px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              marginBottom: '16px',
              transition: 'background 0.2s ease',
            }}
            onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
            onMouseLeave={(e) => e.target.style.background = 'white'}
          >
            <X size={17} color="#807f7e" />
          </button>

          <div style={{ textAlign: 'left' }}>
            <h2 style={{
              fontSize: '16.5px',
              fontWeight: '500',
              color: '#1f2937',
              margin: 0,
              marginBottom: '6px',
            }}>
              Discover Preptune's Tools
            </h2>
            <p style={{
              fontSize: '14px',
              color: '#9ca3af',
              margin: '4px 0 0 0',
              lineHeight: '1.5'
            }}>
              Designed for <em style={{ color: '#60a5fa', fontStyle: 'normal' }}>You</em>, to make your life easier and <em style={{ color: '#60a5fa', fontStyle: 'normal' }}>personalized!</em>
            </p>
          </div>
        </div>

        {/* Scrollable Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '24px',
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '16px',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
        }}>
          {discoverCards.map((card) => (
            <DiscoverCard 
              key={card.id} 
              card={card}
            />
          ))}
        </div>
      </div>

      <style>{`
        div::-webkit-scrollbar {
          width: 8px;
        }

        div::-webkit-scrollbar-track {
          background: transparent;
        }

        div::-webkit-scrollbar-thumb {
          background: #d1d5db;
          borderRadius: 4px;
        }

        div::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </div>
  );
};

// Optimized card component with no animations
const DiscoverCard = React.memo(({ card }) => {
  const [buttonHovered, setButtonHovered] = useState(false);

  return (
    <div>
      <div style={{
        background: 'rgba(255, 255, 255, 0.4)',
        backdropFilter: 'blur(20px)',
        borderRadius: '20px',
        padding: '4px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.71)',
      }}>
        <div style={{
          background: card.gradient,
          borderRadius: '16px',
          padding: '0',
          border: '1px solid rgba(196, 210, 253, 0.5)',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}>
          {/* Top Section */}
          <div style={{
            padding: '20px',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 100%)',
            minHeight: '180px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}>
            {/* User Messages */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              alignItems: 'flex-end',
            }}>
              {card.userMessages.map((msg, idx) => (
                <div 
                  key={idx}
                  style={{
                    background: 'white',
                    borderRadius: '18px',
                    padding: '6px 18px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                    maxWidth: '70%',
                  }}
                >
                  <span style={{
                    fontSize: '11.5px',
                    fontWeight: '500',
                    color: '#1f2937',
                    display: 'block',
                    textAlign: 'center',
                  }}>
                    {msg}
                  </span>
                </div>
              ))}
            </div>

            {/* AI Response */}
            <div style={{
              alignSelf: 'flex-start',
              background: 'rgba(0, 0, 0, 0.85)',
              borderRadius: '18px',
              padding: '6px 18px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              maxWidth: '70%',
            }}>
              <span style={{
                fontSize: '11.5px',
                fontWeight: '500',
                color: 'white',
                display: 'block',
                textAlign: 'center',
              }}>
                {card.aiResponse}
              </span>
            </div>
          </div>

          {/* Bottom Section */}
          <div style={{
            padding: '20px',
            background: 'white',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
          }}>
            {/* Badge */}
            <div style={{
              alignSelf: 'flex-start',
              background: '#f4f4f4ff',
             borderBottom: '1px solid #c2c2c2ff',
              borderRadius: '20px',
              padding: '6px 16px',
            }}>
              <h3 style={{
                fontSize: '10.5px',
                fontWeight: '600',
              color:'#000000ff',
                margin: 0,
              }}>
                {card.title}
              </h3>
            </div>

            {/* Description */}
            <p style={{
              fontSize: '13px',
              color: '#6b7280',
              lineHeight: '1.6',
              margin: 0,
              textAlign: 'left',
            }}>
              {card.description}
            </p>

            <div style={{
              width: '100%',
              height: '1px',
              background: '#e5e7eb',
              margin: '4px 0',
            }} />

            <div style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#141414ff',
              marginBottom: '2px',
              paddingLeft: '4px',
              letterSpacing: '0.05em',
            }}>
              What You Get
            </div>
            
            {/* Features */}
            {card.features.map((feature, idx) => {
              const hasYouTube = feature.toLowerCase().includes("youtube");
              const parts = feature.split(/youtube/i);

              return (
                <div 
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    paddingLeft: '4px',
                  }}
                >
                  <div
                    style={{
                      width: '3px',
                      height: '3px',
                      borderRadius: '50%',
                      background: '#272727',
                      marginTop: '7px',
                      flexShrink: 0,
                    }}
                  />

                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: '400',
                      color: '#2b3341ff',
                      lineHeight: '1.4',
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <span>{parts[0]}</span>

                    {hasYouTube && (
                      <>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          style={{ flexShrink: 0 }}
                        >
                          <path
                            d="M23.5 6.2s-.2-1.7-.9-2.4c-.9-.9-1.9-.9-2.4-1C16.7 2.5 12 2.5 12 2.5h-.1s-4.7 0-8.2.3c-.5.1-1.5.1-2.4 1C.7 4.5.5 6.2.5 6.2S0 8.2 0 10.3v1.9c0 2.1.5 4.1.5 4.1s.2 1.7.9 2.4c.9.9 2.1.9 2.7 1 2 .2 8 .3 8 .3s4.7 0 8.2-.3c.5-.1 1.5-.1 2.4-1 .7-.7.9-2.4.9-2.4s.5-2 .5-4.1v-1.9c0-2.1-.5-4.1-.5-4.1z"
                            fill="#FF0000"
                          />
                          <path
                            d="M9.75 15.02l6-3.51-6-3.51v7.02z"
                            fill="#FFFFFF"
                          />
                        </svg>
                        <span>YouTube</span>
                      </>
                    )}

                    <span style={{ flexBasis: '100%', marginBottom: '-4px' }}>{parts[1]}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
});

DiscoverCard.displayName = 'DiscoverCard';

export default DiscoverModal;